/**
 * erreurEnvoi.js — dire au technicien ce qui a vraiment empêché l'envoi
 *
 * Les trois formulaires affichaient « Erreur lors de la génération du PDF »
 * quoi qu'il arrive : réseau coupé, session expirée, compte rendu refusé par
 * le serveur web. Le message désignait la seule chose qui n'était pas en
 * cause, et il n'y avait rien à en tirer sur le terrain.
 *
 * Le 21/10/2026, le compte rendu de O261005_27555 pesait 47,8 Mo de photos.
 * nginx le refusait à 25 Mo, donc avant même de le transmettre : rien dans les
 * journaux du backend, et le technicien a réessayé quarante et une fois en
 * huit heures en lisant à chaque fois que le PDF ne se générait pas.
 *
 * Le brouillon reste en local dans tous les cas : le dire, pour que personne
 * ne recommence sa saisie de peur de l'avoir perdue.
 */

/**
 * Le poids approximatif de l'envoi, en octets.
 *
 * On additionne la longueur des chaînes déjà en mémoire — les photos sont du
 * base64, un caractère par octet. On évite exprès `JSON.stringify`, qui
 * doublerait l'occupation mémoire d'un envoi de cinquante mégaoctets sur un
 * téléphone qui vient justement d'échouer.
 */
export function poidsApprox(charge) {
  let n = 0;
  const visiter = (v) => {
    if (typeof v === 'string') n += v.length;
    else if (Array.isArray(v)) v.forEach(visiter);
    else if (v && typeof v === 'object') Object.values(v).forEach(visiter);
  };
  try { visiter(charge); } catch { return 0; }
  return n;
}

/** « 47,8 Mo », ou rien si on ne sait pas. */
function enMo(octets) {
  if (!octets) return '';
  return (octets / 1048576).toFixed(1).replace('.', ',') + ' Mo';
}

/**
 * Le message à afficher, selon ce que le serveur a répondu.
 *
 * `defaut` nomme le document attendu (« PDF », « CRI SAV »), pour le cas où
 * l'erreur ne ressemble à rien de connu.
 */
export function messageEnvoi(e, octets, defaut = 'Erreur lors de la génération du PDF') {
  const statut = e && e.response && e.response.status;
  const poids = enMo(octets);

  // Refusé pour sa taille, par le serveur web ou par le backend.
  if (statut === 413) {
    return 'Compte rendu trop lourd pour être envoyé'
      + (poids ? ' (' + poids + ' de photos)' : '')
      + ' : retirez les photos en double, ou appelez la conduite d’activité. '
      + 'Votre saisie est gardée sur le téléphone.';
  }
  if (statut === 401 || statut === 403) {
    return 'Session expirée : reconnectez-vous, votre saisie est gardée sur le téléphone.';
  }
  // Pas de réponse du tout, ou une coupure en cours d'envoi.
  if (!e || !e.response || statut === 408 || statut === 502 || statut === 504) {
    return 'Envoi interrompu'
      + (poids ? ' (' + poids + ' à transmettre)' : '')
      + ' : le réseau n’a pas tenu. Réessayez sous une meilleure couverture — '
      + 'votre saisie est gardée sur le téléphone.';
  }
  // Le backend a parlé : c'est son message qui vaut, pas le nôtre.
  const dit = e.response.data && (e.response.data.error || e.response.data.message);
  if (dit) return String(dit);
  return defaut;
}
